import React, { useState, useEffect } from "react";
import { Typography, Button, Paper, Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import nothingImg from "../../assets/img/nothing.svg"
import { useApp } from "../../context/AppContext";
import { useLanguage } from "../../context/LanguageContext";
import CreateTicketForm from "../SupportTickets/CreateTicketForm";
import TicketItem from "../SupportTickets/TicketItem";
import TicketView from "../SupportTickets/TicketView";

import "../../assets/style/SupportTickets.css";
import "../../assets/style/Styles.css";

export default function SupportTickets() {
  const { tickets = [], loading = false, error = null, fetchTickets } = useApp();
  const [view, setView] = useState('list');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const { t } = useLanguage();

  useEffect(() => {
    const loadTickets = async () => {
      try {
        setLocalLoading(true);
        if (fetchTickets) {
          await fetchTickets();
        }
      } catch (err) {
        console.error('Error loading tickets:', err);
      } finally {
        setLocalLoading(false);
      }
    };

    // Делаем запрос при каждом переходе на страницу
    loadTickets();
  }, []); // Запускаем только один раз при монтировании

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setView('view');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedTicket(null);
  };

  if (view === 'create') {
    return <CreateTicketForm key="create-ticket-form" onBack={handleBackToList} />;
  }

  if (view === 'view' && selectedTicket) {
    return <TicketView key={`ticket-${selectedTicket.ticket_id || selectedTicket.id}`} ticket={selectedTicket} onBack={handleBackToList} />;
  }

  const isLoading = loading || localLoading;

  const hasAnyTicket = Array.isArray(tickets) && tickets.length > 0;

  const redirectToFirstTicket = () => {
    if (!tickets.length) return;

    setSelectedTicket(tickets[0]);
    setView("view");
  };

  return (
    <div className="support-tickets-page" key="support-tickets-main">
      <div className="page-header">
        <Typography variant="h4" className="page-title">
          {t('supportTickets')}
        </Typography>
        <Button
          variant="contained"
          className="create-ticket-button"
          startIcon={<AddIcon />}
          onClick={() => {
            if (hasAnyTicket) {
              setLocalError(
                "You have reached the ticket limit for your account. To continue this conversation, please reopen your previous support ticket. Redirecting you now..."
              );

              setTimeout(() => {
                redirectToFirstTicket();
              }, 3000);

              return;
            }

            setLocalError("");
            setView("create");
          }}
          disabled={isLoading}
        >
          {t('createTicket')}
        </Button>
      </div>
      {localError && (
        <Typography
          color="error"
          sx={{
            mt: 1,
            textAlign: "center",
            fontSize: "17px",
            mb: "15px",
          }}
        >
          {localError}
        </Typography>
      )}

      <div className="tickets-list">
        {isLoading ? (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: { xs: 3, sm: 4 },
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: { xs: '1rem', sm: '1.125rem' }
              }}
            >
              {t('loadingTickets')}
            </Typography>
          </Paper>
        ) : error ? (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: { xs: 3, sm: 4 },
              textAlign: 'center'
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <Typography variant="h6" color="error">{t('error')}: {error}</Typography>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                sx={{ mt: 2 }}
              >
                {t('retry')}
              </Button>
            </Box>
          </Paper>
        ) : tickets.length > 0 ? (
          tickets.map((ticket) => (
            <TicketItem
              key={ticket.id}
              ticket={ticket}
              onClick={() => handleTicketClick(ticket)}
            />
          ))
        ) : (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: { xs: 3, sm: 4 },
              textAlign: 'center'
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{
                width: { xs: 120, sm: 150 },
                height: { xs: 120, sm: 150 },
                opacity: 0.5
              }}>
                <img
                  src={nothingImg}
                  alt="No tickets"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '1.125rem', sm: '1.25rem' }
                }}
              >
                {t('nothingHere')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {/* {t('noTickets')} */}
              </Typography>
            </Box>
          </Paper>
        )}
      </div>
    </div>
  );
}
